import {
  assignmentId as makeAssignmentId,
  addWorkingMinutes,
  calendarId as makeCalendarId,
  dependencyId as makeDependencyId,
  minutes,
  money,
  percent,
  ratePerMinute,
  resourceId as castResourceId,
  taskId as castTaskId,
  subtractWorkingMinutes,
  snapToNextWorkingTime,
  snapToPreviousWorkingTime,
  units,
  workingMinutesBetween,
  type Assignment,
  type BudgetAssignment,
  type Calendar,
  type CalendarException,
  type CalendarId,
  type Dependency,
  type DependencyType,
  type Project,
  type RateTableName,
  type Resource,
  type ResourceId,
  type Task,
  type TaskId,
  type WeekDays,
  InvalidArgumentError,
  buildProjectIndex,
  resolveCalendar,
  validateBudgetAssignments,
} from '@bigint-project/core';
import { assertNoReservedManualId, isSyscalId } from '@bigint-project/calendar-catalog';

import { buildWeekDayPreset } from '../components/calendar/weekday-presets';
import type { PasteImportResult } from '../components/grid/paste-import';
import type { TaskImportPlanRow, XlsxProjectReplacePlan } from '../integration/xlsx-workbook';

/**
 * 純函式 command 層：唯一被允許直接改動 `Project` 的地方。
 * 每個 command 接受目前的 project，回傳「新的」project（或 project 本身，
 * 代表這次呼叫沒有實質變動，store 會因此略過歷史紀錄）。
 *
 * 這一層刻意不呼叫 `packages/core` 的排程／成本/撫平引擎 —— 那些是
 * NotImplementedError 佔位，且不屬於「編輯」語意。實際重新排程由
 * store 的 scheduling 模組在 commit 之後、經 debounce 觸發。
 */

let uidSeq = 1_000_000; // 與 fixtures 的 nextUid() 區間錯開，避免測試混用時撞號。
const nextUid = (): number => {
  uidSeq += 1;
  return uidSeq;
};

// ---------------------------------------------------------------------------
// Task
// ---------------------------------------------------------------------------

export function updateTask(
  project: Project,
  id: TaskId,
  patch: Partial<Omit<Task, 'id'>>,
): Project {
  let changed = false;
  const tasks = project.tasks.map((task) => {
    if (task.id !== id) return task;
    changed = true;
    return { ...task, ...patch };
  });
  if (!changed) return project;
  return { ...project, tasks };
}

export interface InsertTaskParams {
  readonly name: string;
  /** 插入位置的參考任務；未提供則插入到清單尾端（頂層）。 */
  readonly afterTaskId?: TaskId;
  readonly parentId?: TaskId | null;
  readonly overrides?: Partial<Omit<Task, 'id' | 'uid'>>;
}

/** 建立一個欄位齊全、合法的新任務（未排程 —— start/finish 沿用專案開始日）。 */
function buildTask(project: Project, params: InsertTaskParams): Task {
  const uid = nextUid();
  const parentId = params.parentId ?? null;
  const parent = parentId !== null ? project.tasks.find((t) => t.id === parentId) : undefined;
  const outlineLevel = parent !== undefined ? parent.outlineLevel + 1 : 1;
  const anchor = project.startDate;
  return {
    id: castTaskId(`task-${uid}`),
    uid,
    // WBS 一律由結構推導、不在這裡填真實值——store 的收斂點
    // （project-store.ts 的 `withWbsCodes()`）會在這個 task 進到 project
    // 之後立刻覆寫這裡。故意留空字串而不是看似合理但其實是假的值
    // （例如舊版的 `String(uid)`），避免萬一收斂點被繞過時看起來仍然可信。
    wbs: '',
    name: params.name,
    outlineLevel,
    parentId,
    durationMinutes: minutes(480),
    work: minutes(480),
    start: anchor,
    finish: anchor,
    earlyStart: anchor,
    earlyFinish: anchor,
    lateStart: anchor,
    lateFinish: anchor,
    totalFloat: minutes(0),
    freeFloat: minutes(0),
    isCritical: false,
    isSummary: false,
    isMilestone: false,
    isManuallyScheduled: false,
    taskType: project.options?.defaultTaskType ?? 'FixedUnits',
    effortDriven: false,
    constraintType: 'ASAP',
    constraintDate: null,
    deadline: null,
    percentComplete: 0 as Task['percentComplete'],
    percentWorkComplete: 0 as Task['percentWorkComplete'],
    actualStart: null,
    actualFinish: null,
    priority: 500,
    calendarId: null,
    ignoreResourceCalendar: false,
    fixedCost: money(0),
    fixedCostAccrual: 'Prorated',
    notes: '',
    ...params.overrides,
  };
}

export interface InsertTaskResult {
  readonly project: Project;
  readonly taskId: TaskId;
}

/** 新增一筆任務。插在 `afterTaskId` 之後，或附加到清單尾端。 */
export function insertTask(project: Project, params: InsertTaskParams): InsertTaskResult {
  const task = buildTask(project, params);
  if (params.afterTaskId === undefined) {
    return { project: { ...project, tasks: [...project.tasks, task] }, taskId: task.id };
  }
  const index = project.tasks.findIndex((t) => t.id === params.afterTaskId);
  if (index === -1) {
    return { project: { ...project, tasks: [...project.tasks, task] }, taskId: task.id };
  }
  const tasks = project.tasks.slice();
  tasks.splice(index + 1, 0, task);
  return { project: { ...project, tasks }, taskId: task.id };
}

/** 刪除任務（含所有子孫），並清掉引用它們的 dependency / assignment。 */
export function deleteTasks(project: Project, taskIds: readonly TaskId[]): Project {
  if (taskIds.length === 0) return project;
  const toDelete = new Set<string>(taskIds);
  // 遞迴納入子孫任務。
  let grew = true;
  while (grew) {
    grew = false;
    for (const task of project.tasks) {
      if (task.parentId !== null && toDelete.has(task.parentId) && !toDelete.has(task.id)) {
        toDelete.add(task.id);
        grew = true;
      }
    }
  }
  if (toDelete.size === 0) return project;

  const tasks = project.tasks.filter((t) => !toDelete.has(t.id));
  if (tasks.length === project.tasks.length) return project;

  const dependencies = project.dependencies.filter(
    (d) => !toDelete.has(d.predecessorId) && !toDelete.has(d.successorId),
  );
  const assignments = project.assignments.filter((a) => !toDelete.has(a.taskId));

  return { ...project, tasks, dependencies, assignments };
}

/**
 * 縮排：把選取任務的 outlineLevel + 1，parentId 指向清單中緊鄰在前、
 * outlineLevel 相同的任務（該任務同時被標記為 isSummary）。
 * 已經沒有更前面同層任務可依附的項目維持不變。
 */
export function indentTasks(project: Project, taskIds: readonly TaskId[]): Project {
  if (taskIds.length === 0) return project;
  const selected = new Set<string>(taskIds);
  const tasks = project.tasks.slice();
  const parentsToPromote = new Set<string>();

  for (let i = 0; i < tasks.length; i += 1) {
    const task = tasks[i];
    if (task === undefined || !selected.has(task.id)) continue;
    let candidate: Task | undefined;
    for (let j = i - 1; j >= 0; j -= 1) {
      const prev = tasks[j];
      if (prev === undefined) continue;
      if (prev.outlineLevel < task.outlineLevel) break;
      if (prev.outlineLevel === task.outlineLevel && !selected.has(prev.id)) {
        candidate = prev;
        break;
      }
      if (prev.outlineLevel === task.outlineLevel && selected.has(prev.id)) {
        // 前一個同層任務也被選取且會一起縮排：往上找它未來的新 parent。
        continue;
      }
    }
    if (candidate === undefined) continue;
    parentsToPromote.add(candidate.id);
    tasks[i] = { ...task, outlineLevel: task.outlineLevel + 1, parentId: candidate.id };
  }

  if (parentsToPromote.size === 0) return project;
  const finalTasks = tasks.map((t) =>
    parentsToPromote.has(t.id) && !t.isSummary ? { ...t, isSummary: true } : t,
  );
  return { ...project, tasks: finalTasks };
}

/** 外縮：把選取任務的 parentId 換成目前 parent 的 parentId，outlineLevel - 1（下限 1）。 */
export function outdentTasks(project: Project, taskIds: readonly TaskId[]): Project {
  if (taskIds.length === 0) return project;
  const selected = new Set<string>(taskIds);
  const byId = new Map(project.tasks.map((t) => [t.id as string, t] as const));
  let changed = false;

  const tasks = project.tasks.map((task) => {
    if (!selected.has(task.id) || task.outlineLevel <= 1 || task.parentId === null) return task;
    const parent = byId.get(task.parentId);
    changed = true;
    return {
      ...task,
      outlineLevel: Math.max(1, task.outlineLevel - 1),
      parentId: parent?.parentId ?? null,
    };
  });
  return changed ? { ...project, tasks } : project;
}

// ---------------------------------------------------------------------------
// 上下搬移
// ---------------------------------------------------------------------------

/** 非法或缺失的 `outlineLevel` 一律夾制到 1（與 `withWbsCodes()`／`withHierarchy()` 同一套規則）。 */
const normaliseLevel = (level: number): number => {
  if (!Number.isFinite(level)) return 1;
  const truncated = Math.trunc(level);
  return truncated < 1 ? 1 : truncated;
};

/** 以 `index` 為根的子樹在清單中的最後一個位置（子樹在大綱清單中必為連續區段）。 */
function subtreeEndIndex(tasks: readonly Task[], index: number): number {
  const rootTask = tasks[index];
  if (rootTask === undefined) return index;
  const rootLevel = normaliseLevel(rootTask.outlineLevel);
  let end = index;
  while (end + 1 < tasks.length) {
    const next = tasks[end + 1];
    if (next === undefined || normaliseLevel(next.outlineLevel) <= rootLevel) break;
    end += 1;
  }
  return end;
}

export type MoveDirection = 'up' | 'down';

/**
 * 把選取的任務在大綱清單中上移／下移一格，**連同其整棵子樹一起搬**。
 *
 * ## outlineLevel 規則：一律維持不變（同層兄弟互換）
 *
 * 搬移的語義是「在同一個父任務底下與相鄰的兄弟**單元**互換位置」，其中「單元」
 * 指的是一顆任務加上它的整棵子樹。因此：
 *
 * - `outlineLevel` **永不改變**，`parentId` 也永不改變——搬移只重排順序，
 *   不做升降階。改變階層是縮排／凸排（`indentTasks` / `outdentTasks`）的職責。
 * - 上移時跳過的是「前一個兄弟的**整棵子樹**」，不是前一個顯示列。所以把一筆
 *   任務移到一個有 10 個子項的摘要之前，一次就到位，不會中途卡進摘要裡面。
 * - 一筆任務若已經是父任務底下的**第一個**子項就無法再上移、是**最後一個**
 *   就無法再下移（此時沒有可交換的兄弟單元），靜默不動。要離開目前的父任務
 *   請用凸排。
 *
 * 這個選擇（相對於「移動一個顯示列並依落點調整層級」）的理由：後者會讓一次
 * 上移同時改變順序**與**階層，使用者無法只做其中一件事；而且當落點是摘要與其
 * 第一個子項之間時，落下的任務會把原本的子項變成自己的子項，是資料破壞等級的
 * 意外。維持層級則永遠可預測、永遠可逆（上移一次再下移一次必回原位）。
 *
 * ## 多選處理
 *
 * - 選取中若同時包含父與子，只有**最外層**的被視為搬移單元（子已經在父的子樹裡
 *   一起走了），避免同一顆任務被搬兩次。
 * - 上移時由上往下逐一處理、下移時由下往上，且**相鄰兄弟也在選取中時跳過**
 *   ——連續的一群兄弟因此會整群移動一格，而不是彼此互換；已經頂到邊界的那一群
 *   則整群不動。
 * - 不連續的多選各自獨立搬移。
 *
 * 沒有任何一筆實際移動時回傳原本的 project（讓 store 的「reference 相等即無變動」
 * 判斷成立，不會白白產生一筆歷史）。`parentId`／`isSummary`／`wbs` 的收斂交給
 * 呼叫端的 `withHierarchy()` 與 store 的 `withWbsCodes()`。
 */
export function moveTasks(
  project: Project,
  taskIds: readonly TaskId[],
  direction: MoveDirection,
): Project {
  if (taskIds.length === 0 || project.tasks.length === 0) return project;

  const requested = new Set<string>(taskIds);
  const original = project.tasks;

  // 1. 依清單順序取出被選取的位置，並剔除「祖先也被選取」的項目。
  const selectedIndices: number[] = [];
  for (let i = 0; i < original.length; i += 1) {
    const task = original[i];
    if (task !== undefined && requested.has(task.id as string)) selectedIndices.push(i);
  }
  if (selectedIndices.length === 0) return project;

  const rootIds: string[] = [];
  let covered = -1;
  for (const index of selectedIndices) {
    if (index <= covered) continue;
    const task = original[index];
    if (task === undefined) continue;
    rootIds.push(task.id as string);
    covered = subtreeEndIndex(original, index);
  }
  const rootIdSet = new Set(rootIds);

  // 2. 逐一搬移。上移由上往下、下移由下往上——這個順序讓「相鄰兄弟也被選取」
  //    的判斷只需要看選取集合，不必追蹤誰已經移過。
  const working = original.slice();
  const order = direction === 'up' ? rootIds : rootIds.slice().reverse();
  let changed = false;

  for (const rootId of order) {
    const index = working.findIndex((t) => (t.id as string) === rootId);
    if (index === -1) continue;
    const rootTask = working[index];
    if (rootTask === undefined) continue;
    const level = normaliseLevel(rootTask.outlineLevel);
    const end = subtreeEndIndex(working, index);

    if (direction === 'up') {
      // 往前找同層的前一個兄弟；碰到更淺的層級代表已離開目前的父任務。
      let sibling = -1;
      for (let j = index - 1; j >= 0; j -= 1) {
        const candidate = working[j];
        if (candidate === undefined) continue;
        const candidateLevel = normaliseLevel(candidate.outlineLevel);
        if (candidateLevel < level) break;
        if (candidateLevel === level) {
          sibling = j;
          break;
        }
      }
      if (sibling === -1) continue;
      const siblingTask = working[sibling];
      if (siblingTask === undefined || rootIdSet.has(siblingTask.id as string)) continue;
      const block = working.splice(index, end - index + 1);
      working.splice(sibling, 0, ...block);
      changed = true;
      continue;
    }

    // 下移：緊接在子樹之後的那一筆就是下一個兄弟的根（更深不可能，
    // 更淺代表本任務已是父任務底下的最後一個子項）。
    const next = end + 1;
    if (next >= working.length) continue;
    const nextTask = working[next];
    if (nextTask === undefined) continue;
    if (normaliseLevel(nextTask.outlineLevel) !== level) continue;
    if (rootIdSet.has(nextTask.id as string)) continue;
    const nextEnd = subtreeEndIndex(working, next);
    const blockLength = end - index + 1;
    const block = working.splice(index, blockLength);
    // 移除本區塊後，下一個兄弟單元整段往前位移 blockLength。
    working.splice(nextEnd - blockLength + 1, 0, ...block);
    changed = true;
  }

  if (!changed) return project;
  return { ...project, tasks: working };
}

// ---------------------------------------------------------------------------
// Dependency
// ---------------------------------------------------------------------------

export interface AddDependencyParams {
  readonly predecessorId: TaskId;
  readonly successorId: TaskId;
  readonly type?: DependencyType;
  readonly lagMinutes?: number;
}

export function addDependency(project: Project, params: AddDependencyParams): Project {
  if (params.predecessorId === params.successorId) return project;
  const exists = project.dependencies.some(
    (d) => d.predecessorId === params.predecessorId && d.successorId === params.successorId,
  );
  if (exists) return project;
  const dependency: Dependency = {
    id: makeDependencyId(`dep-${params.predecessorId}-${params.successorId}-${nextUid()}`),
    predecessorId: params.predecessorId,
    successorId: params.successorId,
    type: params.type ?? 'FS',
    lagMinutes: minutes(params.lagMinutes ?? 0),
  };
  return { ...project, dependencies: [...project.dependencies, dependency] };
}

export function removeDependency(project: Project, id: Dependency['id']): Project {
  const dependencies = project.dependencies.filter((d) => d.id !== id);
  if (dependencies.length === project.dependencies.length) return project;
  return { ...project, dependencies };
}

// ---------------------------------------------------------------------------
// Resource
// ---------------------------------------------------------------------------

/** 建立資源時可覆寫的欄位；資源的 id / uid 一律由 command 產生，以維持唯一性。 */
export type AddResourceOverrides = Partial<Omit<Resource, 'id' | 'uid'>>;

export interface AddResourceResult {
  readonly project: Project;
  readonly resourceId: ResourceId;
}

/**
 * 取得目前 project 內未使用的資源 UID／ID。
 *
 * 全域序號可避免同一執行期內重複；再以目前 project 的 resources 防禦從外部
 * 匯入、UID 很大的專案或測試手動注入資料，確保新增後兩者仍然唯一。
 */
function nextAvailableResourceUid(project: Project): number {
  const usedUids = new Set(project.resources.map((resource) => resource.uid));
  const usedIds = new Set(project.resources.map((resource) => resource.id as string));
  let uid = nextUid();
  while (usedUids.has(uid) || usedIds.has(`r${uid}`)) {
    uid = nextUid();
  }
  return uid;
}

/**
 * 新增一個完整、可立即指派的 Work 資源。
 *
 * 預設值與 core fixtures 的領域慣例一致；呼叫端可覆寫商業欄位，但無法覆寫
 * id／uid，避免新資源破壞既有資源或 assignment 的識別完整性。
 */
export function addResource(project: Project, overrides: AddResourceOverrides = {}): AddResourceResult {
  const uid = nextAvailableResourceUid(project);
  const id = castResourceId(`r${uid}`);
  const resource: Resource = {
    name: '新增資源',
    type: 'Work',
    initials: '',
    group: '',
    maxUnits: units(1),
    standardRate: ratePerMinute(1),
    overtimeRate: ratePerMinute(1.5),
    costPerUse: money(0),
    accrueAt: 'Prorated',
    calendarId: null,
    materialLabel: null,
    availability: [],
    ...overrides,
    id,
    uid,
  };
  return { project: { ...project, resources: [...project.resources, resource] }, resourceId: id };
}

/** 刪除資源，並在同一次不可分割的 project 更新中刪掉所有相關指派。 */
export function deleteResources(project: Project, resourceIds: readonly ResourceId[]): Project {
  if (resourceIds.length === 0) return project;
  const toDelete = new Set<string>(resourceIds);
  const resources = project.resources.filter((resource) => !toDelete.has(resource.id));
  if (resources.length === project.resources.length) return project;
  const assignments = project.assignments.filter((assignment) => !toDelete.has(assignment.resourceId));
  const budgetAssignments = (project.budgetAssignments ?? []).filter(
    (assignment) => !toDelete.has(assignment.resourceId),
  );
  return { ...project, resources, assignments, budgetAssignments };
}

/**
 * 將資源切換為（或切回）專案摘要預算資源。
 *
 * 預算資源與一般 task assignment 是互斥的：已有一般 assignment 時不得切成
 * 預算資源；切回一般資源時則在同一筆 project 更新中移除它的預算 allocation，
 * 避免留下 core 無法驗證的孤兒資料。無效操作一律回傳原 project，讓 store 不會
 * 產生假的 undo 步驟。
 */
export function setResourceBudgetStatus(
  project: Project,
  resourceId: ResourceId,
  isBudget: boolean,
): Project {
  const resource = project.resources.find((candidate) => candidate.id === resourceId);
  if (resource === undefined) return project;

  if (isBudget && project.assignments.some((assignment) => assignment.resourceId === resourceId)) {
    return project;
  }

  const budgetAssignments = (project.budgetAssignments ?? []).filter(
    (assignment) => assignment.resourceId !== resourceId,
  );
  const statusChanged = resource.isBudget === true ? !isBudget : isBudget;
  const allocationsChanged = !isBudget && budgetAssignments.length !== (project.budgetAssignments?.length ?? 0);
  if (!statusChanged && !allocationsChanged) return project;

  return {
    ...project,
    resources: project.resources.map((candidate) =>
      candidate.id === resourceId ? { ...candidate, isBudget } : candidate,
    ),
    ...(isBudget ? {} : { budgetAssignments }),
  };
}

function sameBudgetAssignments(
  left: readonly BudgetAssignment[] | undefined,
  right: readonly BudgetAssignment[],
): boolean {
  const current = left ?? [];
  return current.length === right.length && current.every((assignment, index) => {
    const candidate = right[index];
    return candidate !== undefined &&
      assignment.id === candidate.id &&
      assignment.resourceId === candidate.resourceId &&
      assignment.budgetWork === candidate.budgetWork &&
      assignment.budgetCost === candidate.budgetCost;
  });
}

/**
 * 原子替換專案摘要預算 allocation。資料先複製並交由 core 驗證；只要任一列
 * 不合法就完整拒絕，絕不套用部分結果。
 */
export function setProjectBudgetAssignments(
  project: Project,
  assignments: readonly BudgetAssignment[],
): Project {
  if (sameBudgetAssignments(project.budgetAssignments, assignments)) return project;
  const budgetAssignments = assignments.map((assignment) => ({ ...assignment }));
  const next = { ...project, budgetAssignments };
  try {
    validateBudgetAssignments(next);
  } catch {
    return project;
  }
  return next;
}

// ---------------------------------------------------------------------------
// Resource assignment
// ---------------------------------------------------------------------------

export interface AssignResourceParams {
  readonly taskId: TaskId;
  readonly resourceId: Assignment['resourceId'];
  readonly units?: number;
  readonly work?: number;
}

/** Change only the assignment's selected cost-rate table. */
export function setAssignmentRateTable(
  project: Project,
  assignmentId: Assignment['id'],
  rateTable: RateTableName,
): Project {
  let changed = false;
  const assignments = project.assignments.map((assignment) => {
    if (assignment.id !== assignmentId || (assignment.rateTable ?? 'A') === rateTable) return assignment;
    changed = true;
    return { ...assignment, rateTable };
  });
  return changed ? { ...project, assignments } : project;
}

/** 指派資源到任務；若該任務/資源已有指派則更新單位與工時。 */
export function assignResource(project: Project, params: AssignResourceParams): Project {
  if (project.resources.find((resource) => resource.id === params.resourceId)?.isBudget) return project;
  const existingIndex = project.assignments.findIndex(
    (a) => a.taskId === params.taskId && a.resourceId === params.resourceId,
  );
  const unitsValue = units(params.units ?? 1);
  const workValue = minutes(params.work ?? 0);
  const task = project.tasks.find((t) => t.id === params.taskId);

  if (existingIndex !== -1) {
    const assignments = project.assignments.slice();
    const existing = assignments[existingIndex];
    if (existing === undefined) return project;
    assignments[existingIndex] = { ...existing, units: unitsValue, work: workValue };
    return { ...project, assignments };
  }

  const assignment: Assignment = {
    id: makeAssignmentId(`asg-${params.taskId}-${params.resourceId}-${nextUid()}`),
    taskId: params.taskId,
    resourceId: params.resourceId,
    rateTable: 'A',
    units: unitsValue,
    work: workValue,
    actualWork: minutes(0),
    remainingWork: workValue,
    start: task?.start ?? project.startDate,
    finish: task?.finish ?? project.startDate,
    cost: money(0),
    actualCost: money(0),
    timephased: null,
  };
  return { ...project, assignments: [...project.assignments, assignment] };
}

export function unassignResource(project: Project, assignmentIdToRemove: Assignment['id']): Project {
  const assignments = project.assignments.filter((a) => a.id !== assignmentIdToRemove);
  if (assignments.length === project.assignments.length) return project;
  return { ...project, assignments };
}

// ---------------------------------------------------------------------------
// Excel / MS Project 貼上匯入
// ---------------------------------------------------------------------------

/**
 * 套用一次「整份 WBS 表貼上」。
 *
 * 語意（見 `TaskGridProps.onTasksPaste`）：
 * - `result.tasks` 自 `result.startIndex`（0-based，對應完整清單）起覆蓋既有列；
 *   超出既有列數的部分**新增**列。
 * - 依賴一律等到所有列都存在之後才建立 —— `predecessors[].target` 的
 *   `kind: 'row'` 指的是本批的第幾列，列還沒建完就解析會產生大量假錯誤。
 *
 * `wbs` 不在這裡填：store 的 `finalizeProjectChange()` 會統一以清單順序 +
 * `outlineLevel` 重算（見 `withWbsCodes()`）。這裡只負責把 `outlineLevel`
 * 與由它推導出來的 `parentId`／`isSummary` 寫對。
 */
export function applyPastedTasks(project: Project, result: PasteImportResult): Project {
  if (result.tasks.length === 0) return project;
  const startIndex = Math.min(Math.max(0, result.startIndex), project.tasks.length);

  // 1. 先讓每一列都有對應的任務。新增列只可能發生在「貼上範圍超出既有列數」時，
  //    此時後面沒有其他既有列，接在尾端即為正確順序。
  let working: Project = project;
  const rowIds: TaskId[] = [];
  for (const row of result.tasks) {
    const existing = project.tasks[startIndex + rowIds.length];
    if (existing !== undefined) {
      rowIds.push(existing.id);
      continue;
    }
    const inserted = insertTask(working, { name: row.name });
    working = inserted.project;
    rowIds.push(inserted.taskId);
  }

  // 2. 只套「來自貼上內容」的欄位；parentId／isSummary 留給步驟 4 對整份清單推導。
  const rowIndexById = new Map<string, number>();
  rowIds.forEach((id, index) => rowIndexById.set(id as string, index));

  const tasks = working.tasks.map((task) => {
    const rowIndex = rowIndexById.get(task.id as string);
    const row = rowIndex === undefined ? undefined : result.tasks[rowIndex];
    if (row === undefined) return task;

    const duration = row.durationMinutes;
    return {
      ...task,
      name: row.name,
      outlineLevel: Math.max(1, row.outlineLevel),
      ...(duration === null
        ? {}
        : { durationMinutes: duration, work: duration, isMilestone: (duration as number) === 0 }),
      ...(row.start === null ? {} : { start: row.start }),
      ...(row.finish === null ? {} : { finish: row.finish }),
    };
  });

  // 3. 所有列都建立完成後才建立依賴。被覆蓋的列先清掉舊的前置任務，
  //    否則貼上結果會是「新舊依賴混在一起」。
  const pastedIds = new Set<string>(rowIds.map((id) => id as string));
  let next: Project = {
    ...working,
    tasks,
    dependencies: working.dependencies.filter((d) => !pastedIds.has(d.successorId)),
  };

  result.tasks.forEach((row, index) => {
    const successorId = rowIds[index];
    if (successorId === undefined) return;
    for (const predecessor of row.predecessors) {
      const predecessorId =
        predecessor.target.kind === 'task'
          ? predecessor.target.taskId
          : rowIds[predecessor.target.rowIndex];
      if (predecessorId === undefined) continue;
      next = addDependency(next, {
        predecessorId,
        successorId,
        type: predecessor.type,
        lagMinutes: predecessor.lagMinutes as number,
      });
    }
  });

  // 4. 階層欄位對**整份最終清單**重新推導（不是只修補被貼上的那一段）。
  return withHierarchy(next);
}

export type XlsxTaskImportMode = 'replace' | 'append';

function importedTaskCalendar(project: Project) {
  const calendarsById = new Map(project.calendars.map((calendar) => [calendar.id as string, calendar] as const));
  const calendar = calendarsById.get(project.defaultCalendarId as string);
  if (calendar === undefined) {
    throw new InvalidArgumentError(`XLSX import: default calendar ${project.defaultCalendarId as string} was not found`);
  }
  return resolveCalendar(calendar, calendarsById);
}

/**
 * Canonicalize the optional start/finish/duration triple using the same working
 * calendar operations as task editing. A supplied triple must already satisfy
 * the calendar identity after normalization; otherwise the whole import is
 * rejected rather than silently retaining contradictory task fields.
 */
function reconcileImportedTaskSchedule(
  project: Project,
  row: TaskImportPlanRow,
): Pick<Task, 'start' | 'finish' | 'durationMinutes'> {
  const calendar = importedTaskCalendar(project);
  const suppliedDuration = row.durationMinutes;
  if (suppliedDuration !== null && (!Number.isFinite(suppliedDuration) || suppliedDuration < 0)) {
    throw new InvalidArgumentError(`XLSX import row ${row.sourceRow}: duration must be a finite non-negative number`);
  }
  const fallbackDuration = 480;

  if (row.start !== null && suppliedDuration !== null) {
    const duration = minutes(suppliedDuration);
    const start = suppliedDuration === 0 ? row.start as Task['start'] : snapToNextWorkingTime(calendar, row.start as Task['start']);
    const finish = addWorkingMinutes(calendar, start, duration);
    if (row.finish !== null && row.finish !== finish) {
      throw new InvalidArgumentError(
        `XLSX import row ${row.sourceRow}: start, finish and duration contradict the working calendar`,
      );
    }
    return { start, finish, durationMinutes: duration };
  }

  if (row.finish !== null && suppliedDuration !== null) {
    const duration = minutes(suppliedDuration);
    const finish = suppliedDuration === 0 ? row.finish as Task['finish'] : snapToPreviousWorkingTime(calendar, row.finish as Task['finish']);
    const start = subtractWorkingMinutes(calendar, finish, duration);
    return { start, finish, durationMinutes: duration };
  }

  if (row.start !== null && row.finish !== null) {
    const start = snapToNextWorkingTime(calendar, row.start as Task['start']);
    const suppliedFinish = snapToPreviousWorkingTime(calendar, row.finish as Task['finish']);
    const spanned = workingMinutesBetween(calendar, start, suppliedFinish) as number;
    if (spanned < 0) {
      throw new InvalidArgumentError(`XLSX import row ${row.sourceRow}: finish is before start in the working calendar`);
    }
    const duration = minutes(spanned);
    return { start, finish: addWorkingMinutes(calendar, start, duration), durationMinutes: duration };
  }

  const duration = minutes(suppliedDuration ?? fallbackDuration);
  if (row.start !== null) {
    const start = snapToNextWorkingTime(calendar, row.start as Task['start']);
    return { start, finish: addWorkingMinutes(calendar, start, duration), durationMinutes: duration };
  }
  if (row.finish !== null) {
    const finish = snapToPreviousWorkingTime(calendar, row.finish as Task['finish']);
    return { start: subtractWorkingMinutes(calendar, finish, duration), finish, durationMinutes: duration };
  }
  const start = snapToNextWorkingTime(calendar, project.startDate);
  return { start, finish: addWorkingMinutes(calendar, start, duration), durationMinutes: duration };
}

/** Apply a fully validated XLSX task plan as one immutable project change. */
export function applyXlsxTaskImport(
  project: Project,
  rows: readonly TaskImportPlanRow[],
  mode: XlsxTaskImportMode,
): Project {
  if (rows.length === 0) return project;

  let working: Project = mode === 'replace'
    ? { ...project, tasks: [], dependencies: [], assignments: [], baselines: [] }
    : project;
  for (const row of rows) {
    const schedule = reconcileImportedTaskSchedule(working, row);
    const duration = schedule.durationMinutes as number;
    const work = row.workMinutes ?? duration;
    const completion = row.percentComplete ?? 0;
    const inserted = insertTask(working, {
      name: row.name,
      overrides: {
        outlineLevel: row.outlineLevel,
        start: schedule.start,
        finish: schedule.finish,
        earlyStart: schedule.start,
        earlyFinish: schedule.finish,
        lateStart: schedule.start,
        lateFinish: schedule.finish,
        durationMinutes: schedule.durationMinutes,
        work: minutes(work),
        isMilestone: duration === 0,
        percentComplete: percent(completion),
        percentWorkComplete: percent(completion),
        fixedCost: money(row.fixedCost ?? 0),
      },
    });
    working = inserted.project;
  }

  return withHierarchy(working);
}

const XLSX_RATE_TABLES = new Set<RateTableName>(['A', 'B', 'C', 'D', 'E']);
const XLSX_DEPENDENCY_TYPES = new Set<DependencyType>(['FS', 'SS', 'FF', 'SF']);
const XLSX_RESOURCE_TYPES = new Set<Resource['type']>(['Work', 'Material', 'Cost']);
const XLSX_ACCRUAL_TYPES = new Set<Resource['accrueAt']>(['Start', 'Prorated', 'End']);
const XLSX_WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

function isPositiveSafeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}

function isFiniteNonNegative(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function isXlsxWallClock(value: unknown): value is string {
  if (typeof value !== 'string' || !XLSX_WALL_CLOCK.test(value)) return false;
  const [datePart, timePart] = value.split('T');
  if (datePart === undefined || timePart === undefined) return false;
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  if (year === undefined || month === undefined || day === undefined || hour === undefined || minute === undefined || second === undefined) return false;
  if (hour > 23 || minute > 59 || second > 59) return false;
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCFullYear() === year && candidate.getUTCMonth() === month - 1 && candidate.getUTCDate() === day;
}

function invalidXlsxProjectImport(detail: string): never {
  throw new InvalidArgumentError(`XLSX project import: ${detail}`);
}

/**
 * Strictly replace the four XLSX entity collections without changing project
 * metadata or calendars. The plan is normally produced by the workbook parser,
 * but this command validates its boundary again so a caller can never persist a
 * partially resolved cross-reference.
 */
export interface XlsxProjectImportOptions {
  /** Explicit authorization to discard snapshots which reference replaced entity IDs. */
  readonly clearProjectSnapshots?: boolean;
}

export function applyXlsxProjectImport(
  project: Project,
  plan: XlsxProjectReplacePlan,
  options: XlsxProjectImportOptions = {},
): Project {
  if (plan.diagnostics.length > 0) invalidXlsxProjectImport('the import plan contains diagnostics');
  if (project.baselines.length > 0 && options.clearProjectSnapshots !== true) {
    invalidXlsxProjectImport('clear baselines explicitly before replacing project data');
  }
  if ((project.budgetAssignments?.length ?? 0) > 0 && options.clearProjectSnapshots !== true) {
    invalidXlsxProjectImport('clear budget assignments explicitly before replacing project data');
  }

  const taskIds = new Map<number, TaskId>();
  for (const task of plan.tasks) {
    if (!isPositiveSafeInteger(task.uid) || taskIds.has(task.uid)) invalidXlsxProjectImport(`invalid or duplicate task UID at row ${String(task.sourceRow)}`);
    if (
      typeof task.name !== 'string' || task.name.trim() === '' ||
      !isPositiveSafeInteger(task.outlineLevel) ||
      !isXlsxWallClock(task.start) || !isXlsxWallClock(task.finish) || task.finish < task.start ||
      !isFiniteNonNegative(task.durationMinutes) || !isFiniteNonNegative(task.workMinutes) ||
      !isFiniteNonNegative(task.percentComplete) || task.percentComplete > 100 ||
      !isFiniteNonNegative(task.fixedCost)
    ) invalidXlsxProjectImport(`invalid task data at row ${String(task.sourceRow)}`);
    taskIds.set(task.uid, castTaskId(`xlsx-task-${String(task.uid)}`));
  }

  const resourceIds = new Map<number, ResourceId>();
  for (const resource of plan.resources) {
    if (!isPositiveSafeInteger(resource.uid) || resourceIds.has(resource.uid)) invalidXlsxProjectImport(`invalid or duplicate resource UID at row ${String(resource.sourceRow)}`);
    if (
      typeof resource.name !== 'string' || resource.name.trim() === '' ||
      !XLSX_RESOURCE_TYPES.has(resource.type) || typeof resource.group !== 'string' ||
      typeof resource.initials !== 'string' || typeof resource.isBudget !== 'boolean' ||
      !isFiniteNonNegative(resource.maxUnits) || !isFiniteNonNegative(resource.standardRatePerMinute) ||
      !isFiniteNonNegative(resource.overtimeRatePerMinute) || !isFiniteNonNegative(resource.costPerUse) ||
      !XLSX_ACCRUAL_TYPES.has(resource.accrueAt) ||
      (resource.materialLabel !== null && typeof resource.materialLabel !== 'string')
    ) invalidXlsxProjectImport(`invalid resource data at row ${String(resource.sourceRow)}`);
    resourceIds.set(resource.uid, castResourceId(`xlsx-resource-${String(resource.uid)}`));
  }

  const tasks: Task[] = plan.tasks.map((row) => {
    const schedule = reconcileImportedTaskSchedule(project, row);
    const duration = schedule.durationMinutes as number;
    return {
      id: taskIds.get(row.uid)!,
      uid: row.uid,
      wbs: '',
      name: row.name.trim(),
      outlineLevel: row.outlineLevel,
      parentId: null,
      durationMinutes: schedule.durationMinutes,
      work: minutes(row.workMinutes as number),
      start: schedule.start,
      finish: schedule.finish,
      earlyStart: schedule.start,
      earlyFinish: schedule.finish,
      lateStart: schedule.start,
      lateFinish: schedule.finish,
      totalFloat: minutes(0),
      freeFloat: minutes(0),
      isCritical: false,
      isSummary: false,
      isMilestone: duration === 0,
      isManuallyScheduled: false,
      taskType: 'FixedUnits',
      effortDriven: false,
      constraintType: 'ASAP',
      constraintDate: null,
      deadline: null,
      percentComplete: percent(row.percentComplete as number),
      percentWorkComplete: percent(row.percentComplete as number),
      actualStart: null,
      actualFinish: null,
      priority: 500,
      calendarId: null,
      ignoreResourceCalendar: false,
      fixedCost: money(row.fixedCost as number),
      fixedCostAccrual: 'Prorated',
      notes: '',
    };
  });

  const resources: Resource[] = plan.resources.map((row) => ({
    id: resourceIds.get(row.uid)!,
    uid: row.uid,
    name: row.name.trim(),
    type: row.type,
    isBudget: row.isBudget,
    initials: row.initials,
    group: row.group,
    maxUnits: units(row.maxUnits),
    standardRate: ratePerMinute(row.standardRatePerMinute),
    overtimeRate: ratePerMinute(row.overtimeRatePerMinute),
    costPerUse: money(row.costPerUse),
    accrueAt: row.accrueAt,
    calendarId: null,
    materialLabel: row.materialLabel,
    availability: [],
  }));

  const assignmentPairs = new Set<string>();
  const assignments: Assignment[] = plan.assignments.map((row) => {
    const taskId = taskIds.get(row.taskUid);
    const resourceId = resourceIds.get(row.resourceUid);
    const resource = plan.resources.find((candidate) => candidate.uid === row.resourceUid);
    const pair = `${String(row.taskUid)}:${String(row.resourceUid)}`;
    if (
      taskId === undefined || resourceId === undefined || resource?.isBudget === true || assignmentPairs.has(pair) ||
      !isFiniteNonNegative(row.units) || !isFiniteNonNegative(row.workMinutes) ||
      !isFiniteNonNegative(row.actualWorkMinutes) || !isFiniteNonNegative(row.remainingWorkMinutes) ||
      row.actualWorkMinutes + row.remainingWorkMinutes !== row.workMinutes ||
      (row.overtimeWorkMinutes !== null && (!isFiniteNonNegative(row.overtimeWorkMinutes) || row.overtimeWorkMinutes > row.actualWorkMinutes)) ||
      !isXlsxWallClock(row.start) || !isXlsxWallClock(row.finish) || row.finish < row.start ||
      !XLSX_RATE_TABLES.has(row.rateTable) || !isFiniteNonNegative(row.cost) ||
      !isFiniteNonNegative(row.actualCost) ||
      (row.remainingCost !== null && !isFiniteNonNegative(row.remainingCost))
    ) invalidXlsxProjectImport(`invalid assignment data at row ${String(row.sourceRow)}`);
    assignmentPairs.add(pair);
    return {
      id: makeAssignmentId(`xlsx-assignment-${String(row.taskUid)}-${String(row.resourceUid)}`),
      taskId,
      resourceId,
      rateTable: row.rateTable,
      units: units(row.units),
      work: minutes(row.workMinutes),
      actualWork: minutes(row.actualWorkMinutes),
      remainingWork: minutes(row.remainingWorkMinutes),
      ...(row.overtimeWorkMinutes === null ? {} : { overtimeWork: minutes(row.overtimeWorkMinutes) }),
      start: row.start as Assignment['start'],
      finish: row.finish as Assignment['finish'],
      cost: money(row.cost),
      actualCost: money(row.actualCost),
      ...(row.remainingCost === null ? {} : { remainingCost: money(row.remainingCost) }),
      timephased: null,
    };
  });

  const dependencyTuples = new Set<string>();
  const dependencies: Dependency[] = plan.dependencies.map((row, index) => {
    const predecessorId = taskIds.get(row.predecessorTaskUid);
    const successorId = taskIds.get(row.successorTaskUid);
    const id = `xlsx-dependency-${String(row.predecessorTaskUid)}-${String(row.successorTaskUid)}-${String(index + 1)}`;
    const tuple = `${String(row.predecessorTaskUid)}:${String(row.successorTaskUid)}:${String(row.type)}:${String(row.lagMinutes)}:${String(row.lagIsPercent)}:${String(row.lagIsElapsed)}`;
    if (
      predecessorId === undefined || successorId === undefined || predecessorId === successorId || dependencyTuples.has(tuple) ||
      !XLSX_DEPENDENCY_TYPES.has(row.type) || !isFiniteNumber(row.lagMinutes) ||
      typeof row.lagIsPercent !== 'boolean' || typeof row.lagIsElapsed !== 'boolean'
    ) invalidXlsxProjectImport(`invalid dependency data at row ${String(row.sourceRow)}`);
    dependencyTuples.add(tuple);
    return {
      id: makeDependencyId(id),
      predecessorId,
      successorId,
      type: row.type,
      lagMinutes: minutes(row.lagMinutes),
      ...(row.lagIsPercent ? { lagIsPercent: true } : {}),
      ...(row.lagIsElapsed ? { lagIsElapsed: true } : {}),
    };
  });

  const candidate = withHierarchy({
    ...project,
    tasks,
    resources,
    assignments,
    dependencies,
    ...(options.clearProjectSnapshots === true ? { baselines: [], budgetAssignments: [] } : {}),
  });
  buildProjectIndex(candidate);
  return candidate;
}

/**
 * 由「清單順序 + `outlineLevel`」完整推導 `parentId` 與 `isSummary`。
 *
 * 與 `withWbsCodes()` 對稱、刻意用同一套心智模型：階層是**推導結果**，不是各自
 * 獨立維護的事實。因此這裡是**雙向**的 —— `parentId` 會改也會清掉，`isSummary`
 * 會設為 true 也會設回 false。
 *
 * 為什麼必須雙向：
 * - 增量修補只加不減時，一個失去所有子任務的舊摘要會保持 `isSummary: true`。
 *   摘要的日期依定義由子任務 rollup 而來，沒有子任務可 roll 的摘要在排程引擎
 *   眼中語義矛盾，會發警告或算出非預期的日期。
 * - 只處理被改動的那一段時，後面沒被覆蓋的任務會沿用舊的父子關係，但它前面的
 *   階層已經被改掉了 —— `parentId` 於是指向錯誤的父任務。
 *
 * 未變動的 Task 沿用原參照（`withWbsCodes()` 同款作法），沒有實質變動時回傳
 * 原本的 project，讓 store 的「reference 相等即無變動」判斷仍然成立。
 */
export function withHierarchy(project: Project): Project {
  /** levelStack[i] = 第 (i + 1) 層目前最後一個任務的 id。 */
  const levelStack: TaskId[] = [];
  const parentIds: (TaskId | null)[] = [];
  const hasChildren = new Set<string>();

  for (const task of project.tasks) {
    const level = Number.isFinite(task.outlineLevel) ? Math.max(1, Math.trunc(task.outlineLevel)) : 1;
    const parentId = level > 1 ? levelStack[level - 2] ?? null : null;
    parentIds.push(parentId);
    if (parentId !== null) hasChildren.add(parentId as string);
    levelStack.length = level - 1;
    levelStack.push(task.id);
  }

  let changed = false;
  const tasks = project.tasks.map((task, index) => {
    const parentId = parentIds[index] ?? null;
    const isSummary = hasChildren.has(task.id as string);
    if (task.parentId === parentId && task.isSummary === isSummary) return task;
    changed = true;
    return { ...task, parentId, isSummary };
  });

  return changed ? { ...project, tasks } : project;
}

// ---------------------------------------------------------------------------
// Project meta（給「專案資訊」對話框使用）
// ---------------------------------------------------------------------------

export type ProjectMetaPatch = Partial<
  Pick<Project, 'name' | 'startDate' | 'finishDate' | 'scheduleFrom' | 'statusDate' | 'currency' | 'notes'>
>;

export function updateProjectMeta(project: Project, patch: ProjectMetaPatch): Project {
  return { ...project, ...patch };
}

// ---------------------------------------------------------------------------
// Calendar（工作日曆維護）
// ---------------------------------------------------------------------------

let calendarSeq = 0;

/** 產生一個不與現有日曆相撞的 id。 */
function nextCalendarId(project: Project): CalendarId {
  const used = new Set(project.calendars.map((calendar) => calendar.id as string));
  for (;;) {
    calendarSeq += 1;
    const candidate = `cal-${calendarSeq}`;
    if (!used.has(candidate)) return makeCalendarId(candidate);
  }
}

/** 未指定任何一天 = 整週都往 base calendar 繼承（見 `WeekDaySpec` 的三態語義）。 */
const INHERIT_ALL_WEEK: WeekDays = [null, null, null, null, null, null, null];

export interface AddCalendarParams {
  readonly name: string;
  readonly baseCalendarId: CalendarId | null;
}

/**
 * 新增一份工作日曆。
 *
 * 有 base calendar 時 `weekDays` 一律留空（七天全 `null`）——整週繼承基礎日曆，
 * 使用者只需覆寫真正不同的那幾天；沒有 base 時給「標準週休二日」當起點，
 * 因為基礎日曆若整週皆 `null` 會被解析成全年不工作，那不是任何人想要的預設值。
 */
export function addCalendar(project: Project, params: AddCalendarParams): { readonly project: Project; readonly calendarId: CalendarId } {
  const baseCalendarId =
    params.baseCalendarId !== null &&
    project.calendars.some((calendar) => calendar.id === params.baseCalendarId)
      ? params.baseCalendarId
      : null;
  const id = nextCalendarId(project);
  const calendar: Calendar = {
    id,
    name: params.name,
    baseCalendarId,
    weekDays: baseCalendarId === null ? buildWeekDayPreset('standard-5-2') : INHERIT_ALL_WEEK,
    exceptions: [],
  };
  return { project: { ...project, calendars: [...project.calendars, calendar] }, calendarId: id };
}

/**
 * 刪除一份工作日曆，並在同一次改動裡把所有指向它的參照收乾淨。
 *
 * 參照處理規則（刪除後專案內不可能存在懸空的 calendarId）：
 * 1. **最後一份日曆不可刪**：專案至少要有一份日曆，`defaultCalendarId` 才有東西可指。
 *    這種情況回傳原 project（無變動 → 不進歷史、不標 dirty）。
 * 2. 任務／資源的 `calendarId` 指向被刪日曆 → 設為 `null`，即「改用專案預設日曆」
 *    （`null` 是領域模型既有的「繼承專案預設」語義，見 `Task.calendarId`）。
 * 3. 其他日曆的 `baseCalendarId` 指向被刪日曆 → 改指向**被刪日曆自己的 base**
 *    （可能是 `null`），繼承鏈接起來而不是斷掉。
 * 4. 被刪的正是 `defaultCalendarId` → 自動改指向剩下的第一份日曆
 *    （規則 1 保證一定還有一份）。
 */
export function deleteCalendar(project: Project, id: CalendarId): Project {
  const target = project.calendars.find((calendar) => calendar.id === id);
  if (target === undefined) return project;
  if (project.calendars.length <= 1) return project;
  if (target.exceptions.some((exception) => isSyscalId(exception.id))) {
    throw new InvalidArgumentError('System-managed calendar exceptions cannot be deleted through the general calendar command');
  }

  const calendars = project.calendars
    .filter((calendar) => calendar.id !== id)
    .map((calendar) =>
      calendar.baseCalendarId === id ? { ...calendar, baseCalendarId: target.baseCalendarId } : calendar,
    );

  const tasks = project.tasks.map((task) => (task.calendarId === id ? { ...task, calendarId: null } : task));
  const resources = project.resources.map((resource) =>
    resource.calendarId === id ? { ...resource, calendarId: null } : resource,
  );

  const fallbackDefault = calendars[0] as Calendar;
  const defaultCalendarId = project.defaultCalendarId === id ? fallbackDefault.id : project.defaultCalendarId;

  return { ...project, calendars, tasks, resources, defaultCalendarId };
}

/** 設定專案預設日曆；id 不存在或本來就是預設時回傳原 project（無變動）。 */
export function setDefaultCalendar(project: Project, id: CalendarId): Project {
  if (project.defaultCalendarId === id) return project;
  if (!project.calendars.some((calendar) => calendar.id === id)) return project;
  return { ...project, defaultCalendarId: id };
}

/** 覆寫單一日曆的每週工作時間。 */
export function updateCalendarWeekDays(project: Project, id: CalendarId, weekDays: WeekDays): Project {
  return updateCalendar(project, id, (calendar) => ({ ...calendar, weekDays }));
}

function sameCalendarException(left: CalendarException, right: CalendarException): boolean {
  if (
    left.id !== right.id ||
    left.name !== right.name ||
    left.from !== right.from ||
    left.to !== right.to ||
    left.working !== right.working ||
    left.workingTimes.length !== right.workingTimes.length
  ) return false;
  for (let index = 0; index < left.workingTimes.length; index += 1) {
    const leftTime = left.workingTimes[index];
    const rightTime = right.workingTimes[index];
    if (leftTime === undefined || rightTime === undefined || leftTime.start !== rightTime.start || leftTime.end !== rightTime.end) {
      return false;
    }
  }
  const leftRecurrence = left.recurrence;
  const rightRecurrence = right.recurrence;
  if (leftRecurrence === undefined || rightRecurrence === undefined) {
    return leftRecurrence === rightRecurrence;
  }
  return leftRecurrence.kind === rightRecurrence.kind &&
    leftRecurrence.interval === rightRecurrence.interval &&
    leftRecurrence.until === rightRecurrence.until;
}

/**
 * 一般 UI／store 的例外日替換只能維護手動例外。
 *
 * `syscal:` 是 catalog service 專用命名空間；一般替換必須逐筆原樣保留既有
 * system exceptions，不能建立、改寫、刪除或複製。未來 catalog 套用需另建
 * 明確、受控的 command/service path，不得以這個一般編輯入口旁路。
 */
function assertSystemExceptionsUnchanged(
  current: readonly CalendarException[],
  candidate: readonly CalendarException[],
): void {
  const currentSystem = new Map<string, CalendarException>();
  for (const exception of current) {
    if (!isSyscalId(exception.id)) continue;
    if (currentSystem.has(exception.id)) {
      throw new InvalidArgumentError(`Duplicate system-managed calendar exception: ${exception.id}`);
    }
    currentSystem.set(exception.id, exception);
  }

  const retainedSystem = new Set<string>();
  const candidateSystemIds: string[] = [];
  let encounteredSystem = false;
  for (const exception of candidate) {
    if (!isSyscalId(exception.id)) {
      assertNoReservedManualId(exception);
      if (encounteredSystem) {
        throw new InvalidArgumentError('Manual calendar exceptions must precede system-managed exceptions');
      }
      continue;
    }
    encounteredSystem = true;
    candidateSystemIds.push(exception.id);
    const existing = currentSystem.get(exception.id);
    if (existing === undefined || retainedSystem.has(exception.id) || !sameCalendarException(existing, exception)) {
      // Use the catalog's canonical reserved-namespace error for every attempted
      // creation, duplicate, or modification through the manual path.
      assertNoReservedManualId(exception);
    }
    retainedSystem.add(exception.id);
  }

  if (retainedSystem.size !== currentSystem.size) {
    throw new InvalidArgumentError('System-managed calendar exceptions cannot be removed through the general calendar command');
  }
  const currentSystemIds = [...currentSystem.keys()];
  if (candidateSystemIds.some((id, index) => id !== currentSystemIds[index])) {
    throw new InvalidArgumentError('System-managed calendar exceptions cannot be reordered through the general calendar command');
  }
}

/** 覆寫單一日曆的手動例外日清單；system-managed 內容必須原樣保留。 */
export function updateCalendarExceptions(
  project: Project,
  id: CalendarId,
  exceptions: readonly CalendarException[],
): Project {
  return updateCalendar(project, id, (calendar) => {
    assertSystemExceptionsUnchanged(calendar.exceptions, exceptions);
    return { ...calendar, exceptions };
  });
}

function updateCalendar(project: Project, id: CalendarId, patch: (calendar: Calendar) => Calendar): Project {
  let changed = false;
  const calendars = project.calendars.map((calendar) => {
    if (calendar.id !== id) return calendar;
    changed = true;
    return patch(calendar);
  });
  return changed ? { ...project, calendars } : project;
}
